import dayjs from 'dayjs';
import { FormFieldProps } from '../../form/FormTypes';
import { useEffect, useRef, useState, JSX } from 'react';
import { FormFieldDatePicker as FormFieldDatePickerProps } from '../../../../component-props';
import { FormfieldContainer, NativeDatePicker } from '@greystar/designsystem';
import { validateMandatory, validateMinMaxDate } from '../../form/FormFieldHelper';

export const FormFieldDatePicker = (
  props: FormFieldDatePickerProps & FormFieldProps
): JSX.Element => {
  const noTime = (date: Date) => {
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const minDate = () => {
    const d = noTime(new Date());
    d.setDate(d.getDate() - props.fields.minDate.value);
    return d;
  };

  const ref = useRef<HTMLDivElement>(null);
  const hiddenRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);
  const datePickerRef = useRef<HTMLInputElement>(null);
  const [date, setDate] = useState<Date>(minDate());
  const [valid, setValid] = useState<boolean>(true);
  const [silent, setSilent] = useState<boolean>(true);

  //Init ref to form
  useEffect(() => {
    props.fieldRefs[props.id as string] = {
      ref,
      isValid,
    };
  }, []);

  const isValid = (d?: Date) => {
    let valid = true;
    if (hiddenRef.current) {
      valid &&= validateMandatory({
        mandatory: props.fields.mandatory.value,
        input: hiddenRef.current,
      });
    }
    if (date) {
      valid &&= validateMinMaxDate({
        date: d ?? date,
        min: props.fields.minDate.value,
        max: props.fields.maxDate.value,
      });
    }
    setValid(valid);
    return valid;
  };

  const maxDate = () => {
    const d = noTime(new Date());
    d.setDate(d.getDate() + props.fields.maxDate.value);
    return d;
  };

  const maxMonthDays = () => {
    const d = new Date(Number(yearRef.current?.value), Number(monthRef.current?.value), 0);
    const max = d.getDate();
    return isNaN(max) ? 31 : max;
  };

  const onBlurFields = () => {
    setSilent(false);
    const d = updateDateFromFields();
    setFields(d);
    isValid(d);
  };

  const onDatePickerChange = () => {
    setSilent(false);
    const d = updateDateFromDatePicker();
    setFields(d);
    isValid(d);
  };

  const updateDateFromFields = () => {
    const d = noTime(new Date());
    d.setDate(Math.min(Number(dayRef.current?.value), maxMonthDays()));
    d.setMonth(Number(monthRef.current?.value) - 1);
    d.setFullYear(Number(yearRef.current?.value));
    setDate(d);
    return d;
  };

  const updateDateFromDatePicker = () => {
    const dateStr = datePickerRef.current?.value ? `${datePickerRef.current?.value}T00:00:00` : '';
    const d = noTime(new Date(dateStr ?? ''));
    setDate(d);
    return d;
  };

  const setFields = (date: Date) => {
    if (dayRef.current) {
      dayRef.current.value = `${date.getDate()}`.padStart(2, '0');
    }
    if (monthRef.current) {
      monthRef.current.value = `${date.getMonth() + 1}`.padStart(2, '0');
    }
    if (yearRef.current) {
      yearRef.current.value = `${date.getFullYear()}`.padStart(4, '20');
    }
    if (datePickerRef.current) {
      datePickerRef.current.value = dayjs(date).format('YYYY-MM-DD');
    }
  };

  return (
    <FormfieldContainer
      ref={ref}
      id={props.id as string}
      label={props.fields.label.value}
      fieldDescription={props.fields.description.value}
      hasDescription={!!props.fields.description.value}
      fieldHint={props.fields.hint.value}
      hasHint={!!props.fields.hint.value}
      fieldErrorMessage={props.fields.errorMessage.value}
      hasError={!valid && (!props.formSilent || !silent)}
      mandatory={props.fields.mandatory.value}
      useFieldset={true}
    >
      <div className="date-picker">
        <div className="date-picker--group">
          <input
            id={`${props.id as string}_hidden`}
            ref={hiddenRef}
            type="hidden"
            name={props.fields.fieldKey.value}
            value={dayjs(date).format('YYYY-MM-DD')}
          />
          <div className="date-picker--group--part-container">
            <label className="form-label text-input--label" htmlFor={`${props.id as string}_month`}>
              {props.fields.monthLabel.value}
            </label>
            <input
              ref={monthRef}
              id={`${props.id as string}_month`}
              type="number"
              name={props.fields.monthFieldKey.value}
              min={1}
              max={12}
              defaultValue={`${date.getMonth() + 1}`.padStart(2, '0')}
              onBlur={onBlurFields}
              aria-required={!!props.fields.mandatory.value}
            />
          </div>
          <div className="date-picker--group--part-container">
            <label className="form-label text-input--label" htmlFor={`${props.id as string}_day`}>
              {props.fields.dayLabel.value}
            </label>
            <input
              ref={dayRef}
              id={`${props.id as string}_day`}
              type="number"
              name={props.fields.dayFieldKey.value}
              min={1}
              max={maxMonthDays()}
              defaultValue={`${date.getDate()}`.padStart(2, '0')}
              onBlur={onBlurFields}
              aria-required={!!props.fields.mandatory.value}
            />
          </div>
          <div className="date-picker--group--part-container">
            <label className="form-label text-input--label" htmlFor={`${props.id as string}_year`}>
              {props.fields.yearLabel.value}
            </label>
            <input
              ref={yearRef}
              id={`${props.id as string}_year`}
              type="number"
              name={props.fields.yearFieldKey.value}
              min={minDate().getFullYear()}
              max={maxDate().getFullYear()}
              defaultValue={`${date.getFullYear()}`.padStart(4, '20')}
              onBlur={onBlurFields}
              aria-required={!!props.fields.mandatory.value}
            />
          </div>
          <div className="date-picker--group--part-container">
            <NativeDatePicker
              ref={datePickerRef}
              onChange={onDatePickerChange}
              name={`${props.fields.fieldKey.value}_native`}
              label="Pick a date"
              nativeInputId={props.id as string}
              min={dayjs(minDate()).format('YYYY-MM-DD')}
              max={dayjs(maxDate()).format('YYYY-MM-DD')}
              skipTab={true}
            />
          </div>
        </div>
      </div>
    </FormfieldContainer>
  );
};
